import { LocalStorageKeys } from "@/constants/local-storage-keys";
import { QueryKeys } from "@/constants/query-keys";
import { useDeleteUser } from "@/hooks/requests/user/use-delete-user";
import { useToast } from "@/hooks/ui/use-toast";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { useIonRouter } from "@ionic/react";
import { useQueryClient } from "@tanstack/react-query";
import { useCloseOnBackButton } from "@/hooks/ui/use-close-on-back-button";
import { memo, useState } from "react";
import { createPortal } from "react-dom";
import { SettingModalHeader } from "../../components/setting-modal-header";

interface DeleteAccountPageProps {
  onBack?: () => void;
  isModal?: boolean;
}

const WARNING_ITEMS = [
  "all your logged meals",
  "all your meals",
  "app preferences",
  "all other data associated to your account",
];

const DeleteAccountPage = memo(function DeleteAccountPage({
  onBack,
  isModal,
}: DeleteAccountPageProps) {
  const navigation = useIonRouter();
  const { mutateAsync: deleteUser, isPending: isDeleting } = useDeleteUser();
  const { showError } = useToast();
  const queryClient = useQueryClient();
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  // Back closes the confirmation, but is swallowed while the delete is running.
  useCloseOnBackButton(showConfirmAlert, () => setShowConfirmAlert(false), {
    canClose: !isDeleting,
  });

  const handleDeleteAccount = async () => {
    try {
      await deleteUser();

      localStorage.removeItem(LocalStorageKeys.USER_KEY);
      localStorage.removeItem(LocalStorageKeys.ACCESS_TOKEN_KEY);
      queryClient.removeQueries({ queryKey: [QueryKeys.USER] });

      navigation.push("/login", "forward", "replace");
    } catch (error) {
      console.error("Failed to delete account:", error);
      if (error instanceof Error) {
        showError(error.message || "Failed to delete account");
      } else {
        showError("Failed to delete account. Please try again.");
      }
    }
  };

  return (
    <>
      <div className="no-scrollbar flex min-h-full w-full flex-col overflow-y-auto bg-white">
        <SettingModalHeader title="Delete Account" onBack={onBack} isModal={isModal} />

        <div className="flex flex-1 flex-col justify-between px-9 pt-10 pb-9">
          {/* Top text */}
          <div className="flex flex-col gap-[21px]">
            <h2 className={cn(textVariants.title2, "font-bold")}>We're sad to see you go!</h2>
            <div className={textVariants.body}>
              <p>Are you sure you want to</p>
              <p>delete your account?</p>
              <p className="mt-4">This action is irreversible.</p>
            </div>
          </div>

          {/* Bottom: warning box + delete button */}
          <div className="flex flex-col gap-4">
            <div
              className={cn(
                "rounded-[27px] border-2 border-[#eb190a] bg-white px-[18px] py-6",
                "drop-shadow-[0px_8px_32.35px_rgba(64,0,255,0.1)] backdrop-blur-[9px]",
              )}
            >
              <p className={textVariants.bodySmall}>
                This action{" "}
                <strong className="font-bold">will permanently delete your account</strong>
                {", including:"}
              </p>
              <ul className="mt-2 list-disc space-y-0.5 pl-4">
                {WARNING_ITEMS.map((item) => (
                  <li key={item} className={textVariants.bodySmall}>
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <button
              type="button"
              onClick={() => setShowConfirmAlert(true)}
              disabled={isDeleting}
              className="flex h-[52px] w-full items-center justify-center rounded-[27px] bg-[#eb190a] px-[18px] shadow-[0px_8px_64.7px_0px_rgba(64,0,255,0.1)] disabled:opacity-60"
            >
              <span className={cn(textVariants.inputText, "text-white")}>Delete Account</span>
            </button>
          </div>
        </div>
      </div>

      {showConfirmAlert &&
        createPortal(
          <div className="animate-modal-fade-in fixed inset-0 z-[10001] flex items-center justify-center px-10">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={!isDeleting ? () => setShowConfirmAlert(false) : undefined}
            />
            <div className="relative flex w-full max-w-[300px] flex-col gap-[10px] rounded-[34px] bg-white p-[14px]">
              <div className="flex flex-col gap-[10px] px-2 pt-2 pb-6 text-center">
                <h2 className={cn(textVariants.title3, "font-bold")}>Delete your account?</h2>
                <p className={textVariants.body}>
                  Are you sure you want to continue with account deletion?
                </p>
              </div>
              <div className="flex flex-col gap-[10px]">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                  className="flex h-12 w-full items-center justify-center rounded-full bg-[#eb190a] px-4 disabled:opacity-60"
                >
                  <span className="text-[17px] font-medium tracking-[-0.43px] text-white">
                    {isDeleting ? "Deleting…" : "Delete account"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setShowConfirmAlert(false)}
                  disabled={isDeleting}
                  className="flex h-12 w-full items-center justify-center rounded-full bg-[rgba(120,120,128,0.16)] px-4 disabled:opacity-60"
                >
                  <span className="text-[17px] font-medium tracking-[-0.43px] text-black">
                    Cancel
                  </span>
                </button>
              </div>
            </div>
          </div>,
          document.querySelector("ion-app") ?? document.body,
        )}
    </>
  );
});

export default DeleteAccountPage;
